import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AccordionModule } from 'primeng/accordion';
import { TreeNode } from 'primeng/api';
import { Checkbox, CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { MultiSelectModule } from 'primeng/multiselect';
import { TreeSelectModule } from 'primeng/treeselect';
import { environment } from '../../../../environments/environment';
import { paths } from '../../../schema/backend';
import { FileSystemService } from '../../../services/file-system.service';
import { Node } from '../../explorer/node';
import { WorkflowService, WorkflowType, SDTMWorkflow, SDTMSelections, USDMWorkflow } from '../../../services/workflow.service';
import { InputTextModule } from 'primeng/inputtext';
import { UploadFileComponent } from '../upload-file/upload-file.component';

type SdtmigDataset = paths["/sdtm/ig"]["get"]["responses"]["200"]["content"]["application/json"];
export type Selections = {
  [category: string]: {
    [dataset: string]: string[];
  };
}

@Component({
  selector: 'app-select-stdm',
  imports: [
    DialogModule,
    CheckboxModule,
    FormsModule,
    CommonModule,
    MultiSelectModule,
    AccordionModule,
    TreeSelectModule,
    InputTextModule,
    UploadFileComponent
  ],
  templateUrl: './select-stdm.component.html',
  styleUrl: './select-stdm.component.css'
})
export class SelectSTDMComponent implements OnInit {
  sdtmigData: SdtmigDataset | null = null;
  sdtmdialogVisible: boolean = false;
  usdmdialogVisible: boolean = false;
  selections: Selections = {};
  fileTree: TreeNode[] = [];
  selectedNodes: TreeNode[] = [];
  selectedStates: { name: string; marked: boolean; required?: boolean }[] = [];
  // selectedStates = signal<{ name: string; marked: boolean; required?: boolean }[]>([]);
  states = [
    // { name: 'Debug', marked: true },
    // { name: 'Debug2', marked: true },
    // { name: 'Debug3', marked: true },
    { name: 'Properties', marked: true },
    { name: 'Arms', marked: true },
    { name: 'Population', marked: true },
    { name: 'Objectives', marked: true },
    { name: 'Endpoints', marked: true },
    { name: 'Elements', marked: true },
    { name: 'SOA Tables', marked: true, required: true },
    { name: 'Epochs & Visits', marked: true },
    { name: 'Activities', marked: true },
    { name: 'Schedule Of Activities', marked: true },
    { name: 'Inclusion/Exclusion Criteria', marked: true },
    { name: 'Download USDM', marked: true, required: true }
  ];

  // USDM inputs
  usdmSelectedNode: TreeNode | null = null;
  osbStudyUid: string = '';
  osbServerUrl: string = '';
  environment = environment;
  rootNode: Node | null = null;
  @Output() submitWorkflow = new EventEmitter<WorkflowType>();

  constructor(private http: HttpClient, private fs: FileSystemService, private wf: WorkflowService) {
    // this.states = this.states.map(state => {
    //   if (!state.name.startsWith('Debug')) {
    //     return { ...state, marked: false };
    //   }
    //   return state;
    // })
  }

  ngOnInit(): void {
    this.loadSDTMDatasets();
    this.selectedStates = this.states.filter(state => state.marked);
    this.fs.rootNodeRefresh$.subscribe(node => {
      // console.log('Root node:', node);
      this.fileTree = this.getFileTree(node);
      this.rootNode = node;
    });
  }

  checkStatesSelected(): void {
    const selectedStateNames = this.selectedStates.map(state => state.name);
    const hasOnlyActivities = selectedStateNames.includes('Activities');
    const hasScheduleOfActivities = selectedStateNames.includes('Schedule Of Activities');

    if (hasOnlyActivities || hasScheduleOfActivities) {
      const epochsAndVisitsState = this.states.find(state => state.name === 'Epochs & Visits');
      if (hasScheduleOfActivities) {
        const activitiesState = this.states.find(state => state.name === 'Activities');
        if (activitiesState && !selectedStateNames.includes('Activities')) {
          const soaIndex = this.selectedStates.findIndex(state => state.name === 'Schedule Of Activities');
          if (soaIndex !== -1) {
            this.selectedStates.splice(soaIndex, 0, { name: 'Activities', marked: true });
          }
          activitiesState.marked = true;
        }
      }
      if (epochsAndVisitsState && !selectedStateNames.includes('Epochs & Visits')) {
        const activityIndex = this.selectedStates.findIndex(state => state.name === 'Activities');
        if (activityIndex !== -1) {
          this.selectedStates.splice(activityIndex, 0, { name: 'Epochs & Visits', marked: true });
        }
        epochsAndVisitsState.marked = true;
      }
    }
    const hasEpochsAndVisits = this.selectedStates.some(state => state.name === 'Epochs & Visits' && state.marked === true);
    if (hasEpochsAndVisits && !selectedStateNames.includes('SOA Tables')) {
      const epochsIndex = this.selectedStates.findIndex(state => state.name === 'Epochs & Visits');
      if (epochsIndex !== -1) {
        this.selectedStates.splice(epochsIndex, 0, { name: 'SOA Tables', marked: true, required: true });
      }
    } else if (!hasEpochsAndVisits && selectedStateNames.includes('SOA Tables')) {
      this.selectedStates = this.selectedStates.filter(state => state.name !== 'SOA Tables');
    }
    this.selectedStates = this.states
      .filter(state => this.selectedStates.some(sel => sel.name === state.name))
      .map(state => {
        const found = this.selectedStates.find(sel => sel.name === state.name);
        return found ? { ...state, ...found } : state;
      }
    );
  }

  getFileTree(node: Node): TreeNode[] {
    return node.items.map(item => ({
      key: item.path,
      label: item.name,
      data: item,
      selectable: !item.isFolder,
      children: item.items.length > 0 ? this.getFileTree(item) : []
    }));
  }

  loadSDTMDatasets(): void {
    this.http.get<SdtmigDataset>(environment.backendUrl + '/sdtm/ig').subscribe({
      next: (data) => {
        // console.log('Loading SDTM datasets...', data);
        this.sdtmigData = data;
        this.sdtmigData?.forEach(category => {
          this.selections[category.name] = {};
          category.datasets.forEach(dataset => {
            this.selections[category.name][dataset.name] = [];
          });
        });
      },
      error: (error) => {
        console.error('Error loading SDTM datasets:', error);
      }
    });
  }

  ensureDatasetSelected(category: string, dataset: string, selectedCols: string[], includeData: Checkbox): void {
    if (!includeData.checked && selectedCols.length > 0) {
      includeData.writeValue(true);
      this.onDatasetCheckboxChange(category, dataset, includeData.checked, false);
    }
  }

  onDatasetCheckboxChange(category: string, dataset: string, checked: boolean, includeDefaults: boolean): void {
    const currentDataset = this.sdtmigData?.find(cat => cat.name === category)?.datasets.find(ds => ds.name === dataset);
    if (!currentDataset) return;
    const requiredColumns = currentDataset.columns.filter(col => col.required);
    const defaultColumns = currentDataset.columns.filter(col => col.default);
    if (checked) {
      // Combine required and default column names and remove duplicates
      const allColumnNames: string[] = [...requiredColumns.map(col => col.name)];
      if (includeDefaults) {
        defaultColumns.forEach(defaultCol => {
          if (!allColumnNames.includes(defaultCol.name)) {
            allColumnNames.push(defaultCol.name);
          }
        });
      }
      const selectedColumns = this.selections[category]?.[dataset] || [];
      selectedColumns.forEach(selectedColName => {
        if (!allColumnNames.includes(selectedColName)) {
          allColumnNames.push(selectedColName);
        }
      });
      this.selections[category] = { ...this.selections[category], [dataset]: allColumnNames };
    } else {
      this.selections[category] = { ...this.selections[category], [dataset]: [] };
    }
  }

  toggle(selectedWorkflow: string) {
    if (selectedWorkflow === 'sdtm') {
      this.sdtmdialogVisible = !this.sdtmdialogVisible;
      if (this.sdtmdialogVisible === true) {
        this.fs.refreshRootNode();
      }
    } else {
      this.usdmdialogVisible = !this.usdmdialogVisible;
      if (this.usdmdialogVisible === true) {
        this.osbServerUrl = environment.osburl;
        this.fs.refreshRootNode();
      }
    }
  }

  close() {
    this.sdtmdialogVisible = false;
    this.usdmdialogVisible = false;
    this.wf.setWorkflow(null);
  }

  onFileUploaded(uploadedFile: Node): void {
    const treeNode: TreeNode = {
      label: uploadedFile.name,
      data: uploadedFile,
      leaf: true
    };
    this.usdmSelectedNode = treeNode;
  }

  onSubmitSdtm(): void {
    // Transform selections to SDTMSelections format
    const finalSelections: SDTMSelections = {};
    Object.keys(this.selections).forEach(category => {
      Object.keys(this.selections[category]).forEach(dataset => {
        const selectedColumns = this.selections[category][dataset];
        if (selectedColumns.length > 0) {
          const key = `${category}_${dataset}`;
          finalSelections[key] = {
            domain: dataset,
            selected_columns: selectedColumns
          };
        }
      });
    });
    const workflow = {
      ...this.wf.workflowOptions.sdtm,
      data: {
        selections: finalSelections,
        specInfo: localStorage.getItem('selectedNodes') || ''
      }
    } as SDTMWorkflow;
    this.submitWorkflow.emit(workflow);
    this.wf.setWorkflow(workflow);
    // console.log(`The Spec files which contain the logic to convert the raw data to SDTM are ${this.selectedNodes.map(node => node?.data?.path).join('\n')}`);
    localStorage.setItem('selectedNodes', `The Spec files which contain the logic to convert the raw data to SDTM is/are ${this.selectedNodes.map(node => node?.data?.path).join('\n')}`);
    this.sdtmdialogVisible = false;
  }

  onSubmitUsdm() {
    if ( !this.usdmSelectedNode) {
      throw new Error('Protocol file selection is required for USDM workflow', {
        cause: { code: 'Protocol_File_Selection' },
      });
    }
    if (!this.osbServerUrl) {
      throw new Error('OSB Server URL is required for USDM workflow', {
        cause: { code: 'OSB_Server_URL' },
      });
    }
    // this.wf.workflowOptions.usdm.data.protocolFilePath = this.usdmSelectedNode?.data?.path;
    this.usdmdialogVisible = false;
    const workflow = {
      ...this.wf.workflowOptions.usdm,
      data: {
      ...this.wf.workflowOptions.usdm.data,
      protocolFilePath: this.usdmSelectedNode?.data?.path,
      osbStudyUid: this.osbStudyUid,
      osbServerUrl: this.osbServerUrl.endsWith('/') ? `${this.osbServerUrl}api` : `${this.osbServerUrl}/api`,
      osbStates: this.selectedStates
      }
    } as USDMWorkflow;
    // console.log(workflow);
    // console.log("states", this.selectedStates)
    this.submitWorkflow.emit(workflow);
    this.wf.setWorkflow(workflow);
  }

  getSelectedCount(category: string, dataset?: string): number {
    if (dataset) {
      return this.selections[category]?.[dataset]?.length || 0;
    } else {
      return Object.values(this.selections[category] || {}).filter(cols => cols.length > 0).length;
    }
  }
}
